// File: src/pages/BigCommerceIntegrationsPage.jsx

import React, { useEffect, useState, useCallback, useContext } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Page, Frame, Toast } from '@shopify/polaris';
import { AuthContext } from '../context/AuthContext';
import { SiteContext } from '../context/SiteContext';
import OnboardingBanner from '../components/OnboardingBanner';
import FlashCardCongrats from '../components/ui/trackerInstalled';
import styles from './IntegrationsPage.module.css';

//const API = process.env.REACT_APP_API_URL || 'https://api.baobrain.com';
const API = 'https://api.baobrain.com';
// Icons
const CheckIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);



const ArrowLeftIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M19 12H5M12 19l-7-7 7-7"/>
  </svg>
);

const ArrowRightIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M5 12h14M12 5l7 7-7 7"/>
  </svg>
);

const RocketIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"/>
    <path d="M12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"/>
  </svg>
);

const AnalyticsIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 3v18h18" />
    <path d="M18 17V9" />
    <path d="M13 17V5" />
    <path d="M8 17v-3" />
  </svg>
);

export default function BigCommerceIntegrationsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { activeSite } = useContext(SiteContext);
  const [siteId, setSiteId] = useState(searchParams.get('site_id'));
  const [siteToken, setSiteToken] = useState(searchParams.get('site_token'));
  const storeHashFromUrl = searchParams.get('store_hash');

  const { token, setToken } = useContext(AuthContext);

  // State for BigCommerce script injection status
  const [loadingBcScripts, setLoadingBcScripts] = useState(false);
  const [bcInjectionStatus, setBcInjectionStatus] = useState(null);

  // State for Toast
  const [message, setMessage] = useState('');
  const [isError, setIsError] = useState(false);
  const [toastActive, setToastActive] = useState(false);

  // Google Analytics Integration States
  const [ga4Status, setGa4Status] = useState({ loading: true, connected: false });
  const [propsList, setPropsList] = useState([]);
  const [propsLoading, setPropsLoading] = useState(false);
  const [propId, setPropId] = useState('');
  const [savedPropId, setSavedPropId] = useState('');
  const [generalGa4Error, setGeneralGa4Error] = useState('');
  const [disconnectingGa4, setDisconnectingGa4] = useState(false);

  // For the Injection Banner
  const [injectionStatus, setInjectionStatus] = useState(null);
  const [injectionMessage, setInjectionMessage] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // Carousel slide control (0 = welcome, 1 = GA4, 2 = activate)
  const [slideIndex, setSlideIndex] = useState(0);

  // Inline confirmation toast — brief mid-step success messages between slides.
  // Pure UI, no logic dependency.
  const [toast, setToast] = useState(null); // { msg: string, key: number }
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  // Fire toast when GA4 connection completes
  const prevConnectedRef = React.useRef(false);
  useEffect(() => {
    if (ga4Status.loading) return;
    if (!prevConnectedRef.current && ga4Status.connected) {
      setToast({ msg: 'Google account connected successfully', key: Date.now() });
    }
    prevConnectedRef.current = ga4Status.connected;
  }, [ga4Status.connected, ga4Status.loading]);

  // Fire toast when tracking gets injected
  const prevInjectedRef = React.useRef(false);
  useEffect(() => {
    const nowInjected = injectionStatus === 'success' || !!bcInjectionStatus;
    if (!prevInjectedRef.current && nowInjected) {
      setToast({ msg: 'Tracking installed automatically — no code added by you', key: Date.now() });
    }
    prevInjectedRef.current = nowInjected;
  }, [injectionStatus, bcInjectionStatus]);

  // Derived state
  const isInjected = injectionStatus === 'success' || bcInjectionStatus;

  // ============================================
  //  ALL ORIGINAL useEffects - PRESERVED
  // ============================================

  useEffect(() => {
    if (activeSite) {
      setSiteId(activeSite.id);
      setSiteToken(activeSite.token);
    }
  }, [activeSite]);

  // Fetch sites from API to ensure we have the site ID for backfill
  useEffect(() => {
    if (!token) return;

    const fetchSitesForBackfill = async () => {
      try {
        const res = await fetch(`${API}/api/sites`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (!res.ok) {
          console.error('[BigCommerce Backfill] Failed to fetch sites:', res.status);
          return;
        }

        const json = await res.json();
        const sites = Array.isArray(json) ? json : (json?.sites || json?.data || []);

        if (sites.length > 0) {
          const bcSite = sites.find(s => s.platform?.toLowerCase() === 'bigcommerce');

          if (bcSite) {
            setSiteId(bcSite.id);
            setSiteToken(bcSite.token);
            sessionStorage.setItem('bigcommerce_site_id', bcSite.id.toString());
            sessionStorage.setItem('bigcommerce_site_token', bcSite.token);
          }
        }
      } catch (err) {
        console.error('[BigCommerce Backfill] Error fetching sites:', err);
      }
    };

    fetchSitesForBackfill();
  }, [token]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get('token');
    if (t) {
      if (!token) {
        setToken(t);
      }
      // Mark OAuth return
      sessionStorage.setItem('bc_oauth_return', 'true');
      
      params.delete('token');
      const newSearch = params.toString();
      const cleanedUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '') + window.location.hash;
      window.history.replaceState({}, '', cleanedUrl);
    }
  }, [token, setToken]);

  // After GA4 status loads, check if we should skip to step 2
  useEffect(() => {
    if (ga4Status.loading) return;
    
    const oauthReturn = sessionStorage.getItem('bc_oauth_return');
    
    if (oauthReturn) {
      sessionStorage.removeItem('bc_oauth_return');
      setSlideIndex(1);
    } else if (ga4Status.connected) {
      setSlideIndex(1);
    }
  }, [ga4Status.loading, ga4Status.connected]);

  // Auto-refresh GA4 status when returning from OAuth
  useEffect(() => {
    const checkForOAuthReturn = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const oauthComplete = urlParams.get('oauth_complete') || urlParams.get('ga4_connected');

      if (oauthComplete) {
        setGa4Status({ loading: true, connected: false });

        try {
          const res = await fetch(`${API}/integrations/status`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const json = await res.json();
          setGa4Status({ loading: false, connected: !!json.connected });

          if (json.connected) {
            const propsRes = await fetch(`${API}/integrations/ga4-properties`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (propsRes.ok) {
              const props = await propsRes.json();
              setPropsList(props);
              if (props.length > 0 && !propId) {
                setPropId(props[0].measurementId);
              }
            }
          }
        } catch (err) {
          setGa4Status({ loading: false, connected: false });
        }

        urlParams.delete('oauth_complete');
        urlParams.delete('ga4_connected');
        const newUrl = `${window.location.pathname}${urlParams.toString() ? '?' + urlParams.toString() : ''}`;
        window.history.replaceState({}, '', newUrl);
      }
    };

    if (token) {
      checkForOAuthReturn();
    }

    // Also refresh when window regains focus
    const handleFocus = async () => {
      if (!token) return;

      try {
        const res = await fetch(`${API}/integrations/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const json = await res.json();
        const wasConnected = ga4Status.connected;
        const isNowConnected = !!json.connected;

        if (wasConnected !== isNowConnected) {
          setGa4Status({ loading: false, connected: isNowConnected });

          if (isNowConnected) {
            const propsRes = await fetch(`${API}/integrations/ga4-properties`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (propsRes.ok) {
              const props = await propsRes.json();
              setPropsList(props);
              if (props.length > 0 && !propId) {
                setPropId(props[0].measurementId);
              }
            }
          }
        }
      } catch (err) {
        console.error('Error checking GA4 status on focus:', err);
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [token, ga4Status.connected, propId]);

  const resetGa4IntegrationStates = useCallback(() => {
    setGa4Status({ loading: true, connected: false });
    setPropsList([]);
    setPropId('');
    setSavedPropId('');
    setGeneralGa4Error('');
    setDisconnectingGa4(false);
  }, []);

  // Fetch GA4 status
  useEffect(() => {
    async function fetchGa4Status() {
      try {
        const res = await fetch(`${API}/integrations/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const json = await res.json();
        setGa4Status({ loading: false, connected: !!json.connected });
      } catch (err) {
        setGa4Status({ loading: false, connected: false });
        setGeneralGa4Error('Failed to connect to backend for GA4 integration status.');
      }
    }
    if (token) fetchGa4Status();
    else setGa4Status({ loading: false, connected: false });
  }, [token]);

  // Fetch saved property
  useEffect(() => {
    if (!ga4Status.connected || !siteId) return;
    (async () => {
      try {
        const res = await fetch(`${API}/integrations/property?site_id=${siteId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const json = await res.json();
        if (json.propertyId) {
          setSavedPropId(json.propertyId);
          setPropId(json.propertyId);
        }
      } catch (err) {
        console.warn('[BigCommerce] No saved GA4 property yet:', err);
      }
    })();
  }, [ga4Status.connected, token, siteId]);

  // Fetch GA4 properties list
  useEffect(() => {
    if (!ga4Status.connected) return;
    (async () => {
      setGeneralGa4Error('');
      setPropsLoading(true);
      try {
        const res = await fetch(`${API}/integrations/ga4-properties`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          const errorText = await res.text();
          throw new Error(`Failed to fetch GA4 properties: ${res.status} - ${errorText}`);
        }
        const props = await res.json();
        setPropsList(props);

        if (props.length > 0) {
          setPropId((current) => current || props[0].measurementId);
        }
      } catch (e) {
        setGeneralGa4Error(`Error fetching GA4 properties: ${e.message}`);
      } finally {
        setPropsLoading(false);
      }
    })();
  }, [ga4Status.connected, token]);

  // Auto-select first property
  useEffect(() => {
    if (ga4Status.connected && propsList.length > 0 && !savedPropId && !propId) {
      setPropId(propsList[0].measurementId);
    }
  }, [ga4Status.connected, propsList, savedPropId, propId]);

  // Initialize page - fetch site info
  useEffect(() => {
    const initializePage = async () => {
      if (activeSite && activeSite.platform?.toLowerCase() === 'bigcommerce') {
        setSiteId(activeSite.id);
        setSiteToken(activeSite.token);
        return;
      }

      if (!siteId || !siteToken) {
        if (!token) return;

        try {
          const response = await fetch(`${API}/api/sites`, {
            headers: { Authorization: `Bearer ${token}` }
          });

          if (!response.ok) throw new Error('Failed to fetch sites list.');

          const sites = await response.json();
          const bcSite = sites.find(s => s.platform?.toLowerCase() === 'bigcommerce');

          if (bcSite) {
            setSiteId(bcSite.id);
            setSiteToken(bcSite.token);
          } else {
            setMessage('No BigCommerce site found. Please connect your store first.');
            setIsError(true);
            setToastActive(true);
          }
        } catch (error) {
          console.error('[BC Init] Error during API fetch:', error);
        }
      }
    };

    initializePage();
  }, [activeSite, siteId, siteToken, token]);

  // ============================================
  //  ALL ORIGINAL HANDLERS - PRESERVED
  // ============================================

  const handleConnectGoogle = () => {
    const nextPath = `/integrations/bigcommerce?site_id=${siteId}&site_token=${siteToken}${storeHashFromUrl ? `&store_hash=${storeHashFromUrl}` : ''}`;
    const oauthUrl = `${API}/auth/google?next=${encodeURIComponent(nextPath)}&linkToken=${encodeURIComponent(token)}`;
    
    if (window.top !== window) {
      window.top.location.href = oauthUrl;
    } else {
      window.location.href = oauthUrl;
    }
  };

  const handleSaveAndInject = useCallback(async () => {
    if (loadingBcScripts) return;

    setLoadingBcScripts(true);
    setGeneralGa4Error('');
    setInjectionStatus(null);
    setInjectionMessage('');

    try {
      // Only save the GA4 property if the user actually selected one
      if (propId) {
        const saveRes = await fetch(`${API}/integrations/property`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ propertyId: propId, site_id: siteId }),
        });
        if (!saveRes.ok) {
          const txt = await saveRes.text();
          throw new Error(`Failed to save property ID: ${txt}`);
        }
        setSavedPropId(propId);
      }

      // Inject BaoBrain Scripts (propertyId is optional)
      const injRes = await fetch(`${API}/bigcommerce/inject`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          site_id: siteId,
          token: siteToken,
          propertyId: propId || null
        })
      });

      const injData = await injRes.json();

      if (!injRes.ok) {
        setInjectionStatus('critical');
        setInjectionMessage(injData.detail || 'GA4 snippet injection failed.');
        setGeneralGa4Error(`Injection failed: ${injData.detail || 'Unknown error'}`);
        return;
      }

      if (injData.injected) {
        setInjectionStatus('success');
        setInjectionMessage('GA4 snippet successfully injected!');
        setBcInjectionStatus(true);
        setShowSuccessModal(true);
      } else {
        setInjectionStatus('success');
        setInjectionMessage(injData.detail || 'GA4 snippet already present with correct ID.');
        setBcInjectionStatus(true);
      }

    } catch (e) {
      console.error('[BigCommerce] handle error', e);
      setInjectionStatus('critical');
      setInjectionMessage(`Error during injection: ${e.message}`);
      setGeneralGa4Error(e.message);
    } finally {
      setLoadingBcScripts(false);
    }
  }, [token, siteId, siteToken, propId, loadingBcScripts]);

  const handleDisconnectGA4 = async () => {
    if (!window.confirm("Are you sure you want to disconnect your Google Analytics account?")) {
      return;
    }

    setDisconnectingGa4(true);
    setGeneralGa4Error('');

    try {
      const res = await fetch(`${API}/integrations/bigcommerce/disconnect-ga4`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ site_id: siteId }),
      });

      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`Disconnect failed: ${txt}`);
      }

      resetGa4IntegrationStates();
      setBcInjectionStatus(false);
      setMessage('Google Analytics account disconnected. Please re-connect.');
      setIsError(false);
      setToastActive(true);

      // Redirect to Google OAuth
      const nextPath = `/integrations/bigcommerce?site_id=${siteId}&site_token=${siteToken}${storeHashFromUrl ? `&store_hash=${storeHashFromUrl}` : ''}`;
      window.location.href = `${API}/auth/google?next=${encodeURIComponent(nextPath)}&linkToken=${encodeURIComponent(token)}`;

    } catch (e) {
      setGeneralGa4Error(`Failed to disconnect: ${e.message}`);
      setDisconnectingGa4(false);
      setMessage(`Failed to disconnect GA4: ${e.message}`);
      setIsError(true);
      setToastActive(true);
    }
  };

  // Navigation
  const goNext = () => setSlideIndex((prev) => Math.min(prev + 1, 2));
  const goBack = () => setSlideIndex((prev) => Math.max(prev - 1, 0));

  const toggleToastActive = useCallback(() => setToastActive((active) => !active), []);
  const toastMarkup = toastActive ? (
    <Toast content={message} error={isError} onDismiss={toggleToastActive} duration={5000} />
  ) : null;

  // ============================================
  //  RENDER
  // ============================================

  if (ga4Status.loading) {
    return (
      <Page title="Set Up BigCommerce">
        <div className={styles.page}>
          <div className={styles.card}>
            <div className={styles.cardBody}>
              <p className={styles.statusText}>Loading...</p>
            </div>
          </div>
        </div>
      </Page>
    );
  }

  // Step Tracker - based on actual progress
  const StepTracker = () => {
    const getStepStatus = (stepNum) => {
      if (stepNum === 1) return 'complete'; // Install always done
      if (stepNum === 2) return ga4Status.connected ? 'complete' : (slideIndex >= 1 ? 'current' : 'pending');
      if (stepNum === 3) return isInjected ? 'complete' : (slideIndex >= 2 ? 'current' : 'pending');
      return 'pending';
    };

    return (
      <div className={styles.stepTracker}>
        {/* Step 1: Install */}
        <div className={styles.stepIndicator}>
          <div className={`${styles.stepIcon} ${styles.complete}`}>
            <CheckIcon />
          </div>
          <span className={`${styles.stepLabel} ${styles.complete}`}>Install</span>
        </div>

        <div className={styles.stepLine}>
          <div className={styles.stepLineProgress} style={{ width: ga4Status.connected ? '100%' : '0%' }} />
        </div>

        {/* Step 2: Connect */}
        <div className={styles.stepIndicator}>
          <div className={`${styles.stepIcon} ${styles[getStepStatus(2)]}`}>
            {ga4Status.connected ? <CheckIcon /> : <AnalyticsIcon />}
          </div>
          <span className={`${styles.stepLabel} ${styles[getStepStatus(2)]}`}>Connect</span>
        </div>

        <div className={styles.stepLine}>
          <div className={styles.stepLineProgress} style={{ width: isInjected ? '100%' : '0%' }} />
        </div>

        {/* Step 3: Activate */}
        <div className={styles.stepIndicator}>
          <div className={`${styles.stepIcon} ${styles[getStepStatus(3)]}`}>
            {isInjected ? <CheckIcon /> : <RocketIcon />}
          </div>
          <span className={`${styles.stepLabel} ${styles[getStepStatus(3)]}`}>Activate</span>
        </div>
      </div>
    );
  };

  // Card 1: Welcome/Onboarding
  const renderWelcomeSlide = () => (
    <div className={styles.slide}>
      <OnboardingBanner
        currentStep={slideIndex + 1}
        onNextStep={goNext}
        onDismiss={() => navigate('/dashboard')}
      />
    </div>
  );

  // Card 2: Connect Google + Select GA4 Property
  const renderGA4Slide = () => (
    <div className={styles.slide}>
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <h2>Add Google Analytics <span className={styles.optionalTag}>Optional</span></h2>
          <p>BaoBrain already tracks your store on its own. Linking GA4 layers in your historical Google data for deeper insights.</p>
        </div>

        <div className={styles.cardBody}>
          {!ga4Status.connected ? (
            <>
              <p className={styles.helpText}>
                If you already use Google Analytics, link it now so BaoBrain can pull your GA4 data in alongside its own tracking.
              </p>
              <button
                className={styles.btnPrimary}
                onClick={handleConnectGoogle}
                disabled={!siteId || !siteToken}
              >
                <GoogleIcon /> Connect Google Account
              </button>
              {(!siteId || !siteToken) && (
                <p className={styles.errorText}>Please ensure Site ID is available to connect GA4.</p>
              )}
              <p className={styles.helpText}>
                Not using Google Analytics? Skip this step — BaoBrain's own tracking captures everything you need.
              </p>
            </>
          ) : propsLoading ? (
            <>
              <div className={styles.loadingSpinner} />
              <p className={styles.statusText}>Looking for your GA4 properties…</p>
              <p className={styles.helpText}>This usually takes a few seconds.</p>
            </>
          ) : (
            <>
              <div className={styles.connectedBadge}>
                <CheckIcon size={16} />
                <span>Google connected — GA4 data will layer in</span>
              </div>

              {propsList.length > 0 ? (
                <>
                  <div className={styles.selectGroup}>
                    <label htmlFor="ga4-property">Choose your Analytics property</label>
                    <select id="ga4-property" value={propId} onChange={(e) => setPropId(e.target.value)}>
                      {propsList.map((p) => (
                        <option key={p.measurementId} value={p.measurementId}>
                          {p.displayName}
                        </option>
                      ))}
                    </select>
                  </div>
                  <p className={styles.helpText}>
                    Don't see the right one?{' '}
                    <a href="https://analytics.google.com/" target="_blank" rel="noopener noreferrer">
                      Open Google Analytics
                    </a>
                  </p>
                </>
              ) : (
                <div className={styles.fallbackBox}>
                  <p className={styles.fallbackTitle}>No Analytics properties on this account</p>
                  <p className={styles.helpText}>
                    No problem — you can skip this step and still get full BaoBrain insights from our pixel.
                  </p>
                  <a
                    href="https://support.google.com/analytics/answer/9304153"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.fallbackLink}
                  >
                    Don't have Google Analytics? Set it up in 2 minutes →
                  </a>
                </div>
              )}

              <button className={styles.btnText} onClick={handleDisconnectGA4} disabled={disconnectingGa4}>
                {disconnectingGa4 ? 'Switching…' : 'Use a different Google account'}
              </button>
            </>
          )}

          {generalGa4Error && <p className={styles.errorText}>{generalGa4Error}</p>}
        </div>

        <div className={styles.cardFooter}>
          <button className={styles.btnSecondary} onClick={goBack}>
            <ArrowLeftIcon /> Back
          </button>
          {ga4Status.connected && !propsLoading && propsList.length === 0 ? (
            <button className={styles.btnPrimary} onClick={goNext}>
              Skip for now <ArrowRightIcon />
            </button>
          ) : (
            <button className={styles.btnPrimary} onClick={goNext} disabled={propsLoading}>
              {propId ? 'Next' : 'Skip for now'} <ArrowRightIcon />
            </button>
          )}
        </div>
      </div>
    </div>
  );

  // Card 3: Activate Tracking
  const renderActivateSlide = () => {
    // Resolve human-readable property name from the dropdown selection
    const selectedProperty = propsList.find(
      (p) =>
        p.measurementId === propId ||
        p.propertyId === propId ||
        String(p.propertyId) === String(propId) ||
        String(p.measurementId) === String(propId)
    );
    const propertyDisplayName = selectedProperty?.displayName || 'your Analytics property';

    return (
    <div className={styles.slide}>
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <h2>{isInjected ? "🎉 You've unlocked your insights" : 'Turn on tracking'}</h2>
          <p>{isInjected ? 'BaoBrain is now watching every visitor, click, and conversion on your store' : 'One last step — turn on tracking for your BigCommerce store'}</p>
        </div>

        <div className={styles.cardBody}>
          {isInjected ? (
            <>
              <div className={styles.successIcon}>
                <CheckIcon size={64} />
              </div>
              <ul className={styles.unlockList}>
                <li><CheckIcon size={16} /> Visitor tracking is live on your store</li>
                <li><CheckIcon size={16} /> Conversion analytics ready to capture orders</li>
                {propId && <li><CheckIcon size={16} /> GA4 connected for deeper traffic insights</li>}
                <li><CheckIcon size={16} /> Customer intelligence dashboards activated</li>
              </ul>
              <p className={styles.helpText} style={{ maxWidth: 360 }}>
                Visit your own store right now and you'll appear in your dashboard within seconds. Richer insights build up as more shoppers visit.
              </p>
            </>
          ) : (
            <>
              {propId ? (
                <div className={styles.summaryBox}>
                  <p className={styles.summaryLabel}>Connected to</p>
                  <p className={styles.summaryValue}>{propertyDisplayName}</p>
                </div>
              ) : (
                <p className={styles.helpText}>
                  You'll be tracking visitor behavior on your store with the BaoBrain pixel.
                </p>
              )}
              <p className={styles.helpText}>
                Click below and we'll set everything up automatically — no code needed.
              </p>

              {injectionStatus === 'warning' && (
                <div className={styles.inlineAlert}>
                  <span className={styles.inlineAlertIcon}>!</span>
                  <span>{injectionMessage}</span>
                </div>
              )}
              {injectionStatus === 'critical' && (
                <div className={styles.inlineAlertError}>
                  <span className={styles.inlineAlertIcon}>!</span>
                  <span>{injectionMessage}</span>
                </div>
              )}
            </>
          )}
        </div>

        <div className={styles.cardFooter}>
          {isInjected ? (
            <>
              <button className={styles.btnSecondary} onClick={handleSaveAndInject} disabled={loadingBcScripts}>
                Re-run setup
              </button>
              <button className={styles.btnPrimary} onClick={() => navigate('/dashboard')}>
                Go to Dashboard
              </button>
            </>
          ) : (
            <>
              <button className={styles.btnSecondary} onClick={goBack}>
                <ArrowLeftIcon /> Back
              </button>
              <button className={styles.btnPrimary} onClick={handleSaveAndInject} disabled={loadingBcScripts}>
                <RocketIcon /> {loadingBcScripts ? 'Setting up…' : 'Turn on tracking'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
    );
  };

  return (
    <Frame>
      <Page
        title="Set up your store"
        backAction={{ content: 'Back to Dashboard', onAction: () => navigate('/dashboard') }}
      >
        <div className={styles.page}>
          <StepTracker />

          {toast && (
            <div className={styles.toast} key={toast.key} role="status" aria-live="polite">
              <span className={styles.toastIcon}>
                <CheckIcon size={14} />
              </span>
              <span>{toast.msg}</span>
            </div>
          )}

          <div className={styles.carousel}>
            <div className={styles.track} style={{ transform: `translateX(-${slideIndex * 100}%)` }}>
              {renderWelcomeSlide()}
              {renderGA4Slide()}
              {renderActivateSlide()}
            </div>
          </div>

          <div className={styles.support}>
            Need help?{' '}
            <a href="https://app.baobrain.com/installation" target="_blank" rel="noopener noreferrer">Setup Guide</a>
            {' · '}
            <a href="/help" target="_blank" rel="noopener noreferrer">Contact Support</a>
          </div>
        </div>

        {/* Success Modal */}
        {showSuccessModal && (
          <div className={styles.modalOverlay} onClick={() => setShowSuccessModal(false)}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
              <FlashCardCongrats
                title="🎉 You've unlocked your insights"
                shopDomain={storeHashFromUrl ? `${storeHashFromUrl}.mybigcommerce.com` : null}
                message={
                  <>
                    <p style={{ marginBottom: 12 }}>
                      BaoBrain is now watching every visitor on your store. Here's what you just turned on:
                    </p>
                    <ul style={{ paddingLeft: 20, margin: '0 0 12px', lineHeight: 1.7 }}>
                      <li>Real-time visitor & session tracking</li>
                      <li>Conversion funnel + drop-off detection</li>
                      <li>Customer intelligence dashboards</li>
                      {savedPropId && <li>GA4 historical data for deeper context</li>}
                    </ul>
                    <p style={{ marginTop: 12, fontSize: 13, color: '#6b7280' }}>
                      💡 <strong>Try it now:</strong> visit your own store in a new tab — you'll show up in your dashboard within seconds.
                    </p>
                    <p style={{ marginTop: 8, fontSize: 13, color: '#6b7280' }}>
                      Smarter pattern-based insights kick in after about <strong>24–72 hours</strong> of traffic.
                    </p>
                  </>
                }
                ctaLabel="Take me to my dashboard"
                onCta={() => { setShowSuccessModal(false); navigate('/dashboard'); }}
                onClose={() => setShowSuccessModal(false)}
                showBackfill={true}
                onBackfill={async () => {
                  try {
                    const siteIdToUse = siteId || Number(sessionStorage.getItem('bigcommerce_site_id'));

                    if (!siteIdToUse) {
                      alert('Could not determine site ID. Please refresh the page and try again.');
                      return;
                    }

                    const res = await fetch(
                      `${API}/api/customer-intelligence/backfill-orders?site_id=${siteIdToUse}&days=365`,
                      {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          Authorization: `Bearer ${token}`,
                        },
                      }
                    );

                    if (!res.ok) {
                      const errorData = await res.json().catch(() => ({}));
                      throw new Error(errorData.detail || 'Failed to start backfill');
                    }

                    alert('Historical order import started! This may take a few minutes.');
                    setShowSuccessModal(false);
                    navigate('/dashboard');

                  } catch (error) {
                    alert('Failed to start backfill: ' + error.message);
                  }
                }}
              />
            </div>
          </div>
        )}
      </Page>
      {toastMarkup}
    </Frame>
  );
}